import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, tokens,
  Field, Input, Button, Spinner,
} from '@fluentui/react-components';
import { Heading } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { API_BASE } from '../config.js';
import { stonePalette } from '../theme/brand';

const CheckCircleIcon = iconMap.check_circle;
const LockIcon = iconMap.lock;
const ErrorIcon = iconMap.error;

const useStyles = makeStyles({
  // minHeight 80vh (ne 100vh) — TAS PATS santykis, koks jau naudojamas Success.jsx
  // (kito panašaus, tikslingai minimalaus tranzakcinio puslapio) — 100vh centruota
  // kortelė palikdavo tiek daug tuščios pilkos erdvės, kad Footer nusistumdavo už
  // matomos srities ribų ir puslapis atrodė kaip "pakibusi kortelė niekur", ne
  // tikras svetainės puslapis (žr. /admin/bugs "Reikia sutvarkyti sio puslapio
  // dizaina, tegul buna kaip DUK, Ekspozicija ar kiti tokio stiliaus").
  page: {
    minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: tokens.spacingHorizontalM, backgroundColor: stonePalette[50],
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadius3XLarge,
    boxShadow: tokens.shadow28, width: '100%', maxWidth: '24rem', padding: tokens.spacingHorizontalXXL,
  },
  iconChip: {
    width: '48px', height: '48px', borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginLeft: 'auto', marginRight: 'auto', marginBottom: tokens.spacingVerticalL,
    boxShadow: '0 4px 12px rgba(9,170,137,0.4)',
  },
  doneWrap: { textAlign: 'center', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  doneText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, margin: 0 },
  doneBtn: { width: '100%', marginTop: tokens.spacingVerticalXS },
  formTitle: { textAlign: 'center', marginBottom: tokens.spacingVerticalXXS },
  formSubtitle: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, textAlign: 'center', marginBottom: tokens.spacingVerticalXXL, marginTop: 0 },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalM, backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`, borderRadius: tokens.borderRadiusLarge,
    fontSize: tokens.fontSizeBase300, color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalL,
  },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  submitBtn: { width: '100%', marginTop: tokens.spacingVerticalXS },
});

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const s = useStyles();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const [error, setError] = useState('');

  // Scroll to top
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError(t('reset_password.mismatch')); return; }
    if (password.length < 8) { setError(t('reset_password.too_short')); return; }

    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('reset_password.generic_error'));
      setStatus('done');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  return (
    <div className={s.page}>
      <div className={s.card}>

        <div className={s.iconChip}>
          {status === 'done' ? <CheckCircleIcon fontSize={24} /> : <LockIcon fontSize={24} />}
        </div>

        {status === 'done' ? (
          <div className={s.doneWrap}>
            <Heading as="h2" variant="h2">{t('reset_password.done_title')}</Heading>
            <p className={s.doneText}>
              {t('reset_password.done_text')}
            </p>
            <Button appearance="primary" className={s.doneBtn} onClick={() => navigate('/')}>
              {t('reset_password.back_home')}
            </Button>
          </div>
        ) : (
          <>
            <Heading as="h2" variant="h2" className={s.formTitle}>{t('reset_password.form_title')}</Heading>
            <p className={s.formSubtitle}>
              {t('reset_password.form_subtitle')}
            </p>

            {error && (
              <div className={s.errorBox}>
                <ErrorIcon fontSize={16} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className={s.form}>
              <Field label={t('reset_password.password_label')} required>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('reset_password.password_ph')}
                  required
                  autoFocus
                />
              </Field>
              <Field label={t('reset_password.confirm_label')} required>
                <Input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder={t('reset_password.confirm_ph')}
                  required
                />
              </Field>
              <Button
                type="submit"
                appearance="primary"
                disabled={status === 'loading'}
                className={s.submitBtn}
                icon={status === 'loading' ? <Spinner size="tiny" /> : undefined}
              >
                {status === 'loading' ? null : t('reset_password.submit')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
